import { useQuery } from '@tanstack/react-query'
import { statusApi, type StatusBannerItem } from '../api'
import { Icon } from '../ui'

/*
 * The cabinet's strip about the status page (OPS-1, docs/status-page.md):
 * open incidents and maintenance that touch this user's components (the
 * server picks them: their ingest region, delivery when they have
 * destinations, captions when the feature is on). Nothing when all is well,
 * when the page is off or does not answer.
 */

const STATUS_TEXT: Record<string, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
}

function when(iso?: string) {
  return iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''
}

/** The system's banner (ToastsAndBanners): incident = fail if critical else warn, maintenance = idle. */
function state(i: StatusBannerItem): 'fail' | 'warn' | 'idle' {
  if (i.kind === 'maintenance') return 'idle'
  return i.impact === 'critical' ? 'fail' : 'warn'
}

export function StatusBanner() {
  const q = useQuery({ queryKey: ['status-banner'], queryFn: statusApi.mine, refetchInterval: 60_000, staleTime: 30_000, retry: false })
  const items = q.data?.items ?? []
  if (items.length === 0) return null
  const page = q.data?.page_url
  return (
    <div className="w-full" data-testid="status-banner">
      {items.map((i) => (
        <div key={i.id} role={i.kind === 'incident' ? 'alert' : 'status'} className="sx-banner flex-wrap px-4 md:px-8" data-state={state(i)}>
          <Icon name={`state-${state(i)}`} size={16} />
          <span className="txt">
            <b>{i.kind === 'maintenance' ? 'Maintenance' : 'Incident'}:</b> {i.title}
          </span>
          <span className="tm">
            {STATUS_TEXT[i.status] ?? i.status}
            {i.kind === 'maintenance' && i.starts_at ? ` · ${when(i.starts_at)} – ${when(i.ends_at)}` : ''} · {i.components.join(', ')}
          </span>
          {page && (
            <a href={page} target="_blank" rel="noreferrer">
              Details
            </a>
          )}
        </div>
      ))}
    </div>
  )
}
